<script setup lang="ts">
import {
  fetchSignInMethodsForEmail,
  linkWithCredential,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth'
import {
  auth,
  facebookAuthProvider,
  googleAuthProvider,
  twitterAuthProvider,
} from '@/firebase'
import { useRootStore } from '@/stores/root'
import { createUser, getUser } from '@/services/userService'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { capitalizeString } from '@/utils/stringHelpers'
import { reactive, ref } from 'vue'
import ConfirmationDialog from '@/components/base/ConfirmationDialog.vue'
import PopupModal from '@/components/base/PopupModal.vue'
import { requiredValidator } from '@validators'
import { AuthProvidersEnum } from '@/common/enums/authProvidersEnum'
import { ElementIDs } from '@/common/enums/elementIdsEnum'
import googleIcon from '@/assets/images/icons/v2-design/google-icon.svg'
import { transformToUserProfileModel } from '@/utils/objectTransformers/user'
import { UserAccountType } from '@/common/constants/userContants'

interface AuthProvider {
  icon: string
  color: string
  authProvider: 'google' | 'facebook' | 'twitter'
  id: string
}

const route = useRoute()
const router = useRouter()
const rootStore = useRootStore()
const userStore = useUserStore()

const existingAccountData = ref()
const showMergeAccountsDialog = ref(false)
const showPasswordDialog = ref(false)
const isPasswordVisible = ref(false)
const accountPassword = ref('')
const formErrors = reactive({
  password: undefined,
})
const authProviders: AuthProvider[] = [
  {
    icon: googleIcon,
    color: '#dd4b39',
    authProvider: 'google',
    id: ElementIDs.AUTH.AUTH_PROVIDER.GOOGLE,
  },
]

type ProviderId = 'google' | 'facebook' | 'twitter'
const getProviderFromProviderId = (pId: ProviderId) =>
  pId === 'twitter'
    ? twitterAuthProvider
    : pId === 'facebook'
    ? facebookAuthProvider
    : googleAuthProvider

async function signInWithAuthProvider(providerStr: ProviderId) {
  rootStore.showFullscreenLoader = true
  const authProvider = getProviderFromProviderId(providerStr)
  signInWithPopup(auth, authProvider)
    .then(async (res) => {
      let isUserBlock = false
      try {
        const userData = await getUser()
        console.log('userData', userData)
        if (
          !!userData &&
          userData?.status === 'active' &&
          userData?.isApproved &&
          userData?.signupApprovalStatus === 'approved'
        ) {
          userStore.setUserProfile(userData)
          await router.replace(route.query.to ? String(route.query.to) : '/')
        } else if (
          !!userData &&
          !userData?.isApproved &&
          userData?.signupApprovalStatus === 'pending'
        ) {
          isUserBlock = true
          throw new Error(
            'Your account is pending for approval. Please contact customer support',
          )
        } else if (
          !!userData &&
          !userData?.isApproved &&
          userData?.signupApprovalStatus === 'declined'
        ) {
          isUserBlock = true
          throw new Error(
            'Your account request has been declined after signup. Please contact customer support if you think it was a mistake',
          )
        } else {
          throw new Error(
            'Your account has been blocked. Please contact customer support',
          )
        }
      } catch (e: any) {
        const user = res.user
        if (e?.response?.status === 404) {
          try {
            const userData = await createUser({
              uuid: user.uid,
              email: user.email!,
              firstName: user.displayName!,
              phoneNumber: user.phoneNumber,
              signupSource: 'social login',
              affiliateToken: route.query.via as string,
              noOfSites: 0,
            })
            if (
              !!userData &&
              userData?.status === 'active' &&
              userData?.isApproved &&
              userData?.signupApprovalStatus === 'approved'
            ) {
              userStore.setUserProfile(userData)
              await router.replace(
                route.query.to ? String(route.query.to) : '/',
              )
            } else if (
              !!userData &&
              !userData?.isApproved &&
              userData?.signupApprovalStatus === 'pending'
            ) {
              isUserBlock = true
              throw new Error(
                'Your account is pending for approval. Please contact customer support',
              )
            } else if (
              !!userData &&
              !userData?.isApproved &&
              userData?.signupApprovalStatus === 'declined'
            ) {
              isUserBlock = true
              throw new Error(
                'Your account request has been declined after signup. Please contact customer support if you think it was a mistake',
              )
            } else {
              throw new Error(
                'Your account has been blocked. Please contact customer support',
              )
            }
          } catch (e: any) {
            console.log('------', e)
            if (!isUserBlock) {
              res?.user?.delete()
            }
            if (isUserBlock) {
              auth?.signOut?.()
            }
            rootStore.showSnackbar(
              isUserBlock
                ? e?.message || 'Failed to login'
                : 'Failed to login. Please try again later.',
              'error',
            )
          }
        } else {
          throw e
        }
      }
    })
    .catch((error: any) => {
      console.log('error', error)
      if (error.code === 'auth/account-exists-with-different-credential') {
        existingAccountData.value = error.customData
        showMergeAccountsDialog.value = true
        return
      } else {
        if (auth.currentUser) userStore.logout()
        rootStore.showSnackbar(
          error.code
            ? `Failed to login. ${
                error.code
                  ? error.code
                      .split('/')
                      .pop()
                      .split('-')
                      .map((str: string) => capitalizeString(str))
                      .join(' ')
                  : null
              }`
            : error.message,
          'error',
        )
      }
    })
    .finally(() => {
      rootStore.showFullscreenLoader = false
      localStorage.setItem('provider-used-to-login', AuthProvidersEnum.SOCIAL)
    })
}

const linkAccountIfExistingMethodIsPassword = async () => {
  try {
    rootStore.showFullscreenLoader = true
    const existingAccount = existingAccountData.value
    const email = existingAccount.email
    const credential = OAuthProvider.credentialFromResult(existingAccount)
    const { user } = await signInWithEmailAndPassword(
      auth,
      email,
      accountPassword.value,
    )
    if (user && credential) {
      const res = await linkWithCredential(user, credential)
      if (res) {
        const user = await getUser()
        userStore.setUserProfile(user)
        await router.replace(route.query.to ? String(route.query.to) : '/')
      }
    }
  } catch (e) {
    rootStore.showSnackbar(
      'Failed to merge/link accounts. Please try again.',
      'error',
    )
    if (auth.currentUser) await userStore.logout()
  } finally {
    rootStore.showFullscreenLoader = false
  }
}
const linkAccounts = async () => {
  try {
    rootStore.showFullscreenLoader = true
    const existingAccount = existingAccountData.value
    const email = existingAccount.email
    const pendingCred = OAuthProvider.credentialFromResult(existingAccount)
    const availableSignInMethods = await fetchSignInMethodsForEmail(auth, email)
    if (availableSignInMethods.includes('password')) {
      showPasswordDialog.value = true
      return
    }
    // ELSE: link social accounts
    const authProviderId = availableSignInMethods[0].split('.')[0]
    const provider = getProviderFromProviderId(authProviderId as ProviderId)
    const { user } = await signInWithPopup(auth, provider)
    if (user) {
      const userCreds = await linkWithCredential(user, pendingCred!)
      if (userCreds) {
        const user = await getUser()
        userStore.setUserProfile(user)
        await router.replace(route.query.to ? String(route.query.to) : '/')
      }
    }
  } catch (error: any) {
    rootStore.showSnackbar(
      'Failed to merge/link accounts. Please try again.',
      'error',
    )
    if (auth.currentUser) await userStore.logout()
  } finally {
    rootStore.showFullscreenLoader = false
  }
}
</script>

<template>
  <div class="d-flex justify-center flex-wrap gap-3">
    <ConfirmationDialog
      v-model:is-dialog-visible="showMergeAccountsDialog"
      width="400"
      :on-confirm="linkAccounts"
      title="Link Accounts"
      message="You already have an account with this email. Do you want to link your accounts?"
    />
    <PopupModal
      v-model:is-dialog-visible="showPasswordDialog"
      width="450"
      title="Enter your password"
      :on-confirm="linkAccountIfExistingMethodIsPassword"
      confirm-btn-text="Link Account"
    >
      <template #body>
        <VTextField
          v-model="accountPassword"
          label="Password"
          :rules="[requiredValidator]"
          :type="isPasswordVisible ? 'text' : 'password'"
          :error-messages="formErrors.password as string"
          :append-inner-icon="
            isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'
          "
          @click:append-inner="isPasswordVisible = !isPasswordVisible"
        />
      </template>
    </PopupModal>
    <VBtn
      v-for="(link, idx) in authProviders"
      :id="link.id"
      :key="idx"
      variant="outlined"
      color="secondary"
      elevation="0"
      rounded="0"
      block
      @click="signInWithAuthProvider(link.authProvider)"
    >
      <VImg size="28" class="v-icon" :src="link.icon" alt="google icon" />
      <span class="mx-5 font-weight-semibold" style="text-transform: none">
        Continue with
        <span class="text-capitalize">{{ link.authProvider }}</span>
      </span>
    </VBtn>
  </div>
</template>
